import { useMemo } from 'react';
import { Search, Loader2, ChevronRight, Sparkles } from 'lucide-react';
import Dropdown from '../../../components/receptura/Dropdown';
import RadekMapovani from './RadekMapovani';
import { AKCENT as SECTION_COLOR } from '../../../lib/spizirnaUi';
import { doSkupiny } from './skupiny';

const STAVY = [
    { value: 'ceka', label: 'Čeká na rozhodnutí' },
    { value: 'potvrzeno', label: 'Hotové' },
    { value: 'ignorovat', label: 'Ignorované' },
    { value: 'vse', label: 'Vše' },
];

const TYPY = [
    { value: '', label: 'Produkty i ingredience' },
    { value: 'produkt', label: 'Jen produkty' },
    { value: 'pridavek', label: 'Jen ingredience' },
];

/**
 * Seznam názvů k rozhodnutí.
 *
 * Dvě podoby podle normy §1: **široký = přehled**, kde se kreslí mřížka
 * skupin, a **úzký**, kde běží plochý seznam vedle detailu.
 *
 * Přehled je po skupinách z Objedname schválně. Objedname combo nezná
 * a obchází ho přes přídavky, takže skupina („Nápoj výhodně", „Příloha
 * výhodně", „Vyberte si svou pizzu") je jediné místo, kde je ta struktura
 * vidět — a rozhoduje se podle ní po deseti položkách naráz, ne po jedné.
 */
export default function SeznamParovani({
    siroky, polozky, nacitam, styly, isDark,
    stav, onStav, typ, onTyp, hledani, onHledani,
    skupina, onSkupina, vybranyId, onVyber, onPotvrdit, akce,
}) {
    const { secondaryColor, mutedColor, cardStyle, inputStyle } = styly;

    const skupiny = useMemo(() => {
        const mapa = new Map();
        for (const p of polozky) {
            const k = doSkupiny(p);
            if (!mapa.has(k)) mapa.set(k, { nazev: k, pocet: 0, trzba: 0, nove: 0 });
            const s = mapa.get(k);
            s.pocet++;
            s.trzba += p.trzba || 0;
            if (p.nove) s.nove++;
        }
        return [...mapa.values()].sort((a, b) => b.trzba - a.trzba || b.pocet - a.pocet);
    }, [polozky]);

    const videt = useMemo(
        () => (skupina ? polozky.filter((p) => doSkupiny(p) === skupina) : polozky),
        [polozky, skupina],
    );

    const lista = (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2"
            style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)'}`, flexShrink: 0 }}>
            <div style={{ position: 'relative', flex: '1 1 160px', minWidth: 150 }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: mutedColor }} />
                <input placeholder="Hledat název…" value={hledani} onChange={(e) => onHledani(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 27, fontSize: 12, height: 32 }} />
            </div>
            <Dropdown value={stav} onChange={onStav} options={STAVY} isDark={isDark}
                height={32} minWidth={158} accent={SECTION_COLOR} />
            <Dropdown value={typ} onChange={onTyp} options={TYPY} isDark={isDark}
                height={32} minWidth={158} accent={SECTION_COLOR} />
            {siroky && akce}
        </div>
    );

    return (
        <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={{ ...cardStyle, minHeight: 0 }}>
            {lista}

            {/* Cesta zpět na přehled — klik na skupinu, jak žádá norma §1. */}
            {skupina && (
                <button onClick={() => onSkupina(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-bold text-left"
                    style={{
                        background: `${SECTION_COLOR}12`, border: 'none', cursor: 'pointer',
                        color: SECTION_COLOR, flexShrink: 0,
                    }}>
                    <ChevronRight size={13} style={{ transform: 'rotate(180deg)' }} />
                    {skupina}
                    <span style={{ color: mutedColor, fontWeight: 400 }}>· zpět na skupiny</span>
                </button>
            )}

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {nacitam ? (
                    <div className="flex justify-center py-12">
                        <Loader2 size={20} className="animate-spin" style={{ color: mutedColor }} />
                    </div>
                ) : polozky.length === 0 ? (
                    <div className="text-center py-12 text-sm" style={{ color: mutedColor }}>
                        {stav === 'ceka' ? 'Nic nečeká — všechno živé je rozhodnuté.' : 'Žádné názvy.'}
                    </div>
                ) : siroky && !skupina ? (
                    <MrizkaSkupin skupiny={skupiny} onVybrat={onSkupina} styly={styly} isDark={isDark} />
                ) : (
                    videt.map((m) => (
                        <RadekMapovani key={m.id} radek={m} vybrany={vybranyId === m.id}
                            isDark={isDark} styly={styly}
                            // Uvnitř skupiny je název skupiny u každého řádku šum —
                            // stojí nad seznamem a v úzkém sloupci bere místo názvu.
                            skupinaSkryta={!!skupina}
                            onVybrat={() => onVyber(m)} onPotvrdit={() => onPotvrdit(m)} />
                    ))
                )}
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 text-[10.5px]"
                style={{
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                    color: mutedColor, flexShrink: 0,
                }}>
                <span>
                    {skupina ? `${videt.length} z ${polozky.length}` : `${polozky.length}`}{' '}
                    {polozky.length === 1 ? 'název' : polozky.length < 5 ? 'názvy' : 'názvů'}
                </span>
                <span className="ml-auto tnum" style={{ color: secondaryColor }}>
                    {Math.round(videt.reduce((s, p) => s + (p.trzba || 0), 0)).toLocaleString('cs-CZ')} Kč / 90 dní
                </span>
            </div>
        </div>
    );
}

/**
 * Přehled skupin. Norma §1 zakazuje poloprázdnou plochu s hláškou „vyberte
 * položku" — dokud není co ukazovat v detailu, patří šířka seznamu, a tohle
 * je to nejužitečnější, co na ní může být.
 */
function MrizkaSkupin({ skupiny, onVybrat, styly, isDark }) {
    const { mutedColor, secondaryColor } = styly;

    return (
        <div className="grid gap-2 p-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
            {skupiny.map((s) => (
                <button key={s.nazev} onClick={() => onVybrat(s.nazev)}
                    className="flex flex-col gap-1 p-3 rounded-xl text-left"
                    style={{
                        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                        cursor: 'pointer',
                    }}>
                    <div className="flex items-start gap-2">
                        <span className="text-[13px] font-semibold flex-1 min-w-0"
                            style={{ color: 'var(--text-primary)' }}>
                            {s.nazev}
                        </span>
                        <span className="text-[17px] font-bold tnum" style={{ color: 'var(--text-primary)' }}>
                            {s.pocet}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]" style={{ color: mutedColor }}>
                        <span className="tnum" style={{ color: secondaryColor }}>
                            {Math.round(s.trzba).toLocaleString('cs-CZ')} Kč
                        </span>
                        {s.nove > 0 && (
                            <span className="inline-flex items-center gap-1 ml-auto font-bold"
                                style={{ color: SECTION_COLOR }}>
                                <Sparkles size={10} />
                                {s.nove} nové
                            </span>
                        )}
                    </div>
                </button>
            ))}
        </div>
    );
}
